import { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../services/firebase";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError("Failed to send password reset email. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="relative flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat animate-fade-in"
        style={{
          backgroundImage: `url('/cathsbgfinal.png')`,
        }}
      >
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/60 via-primary-900/40 to-neutral-900/60"></div>

        <div className="relative z-10 w-full max-w-sm mx-4 animate-scale-in">
          {/* Success Card */}
          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 bg-success-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-4">
              Check Your Email
            </h1>

            <p className="text-neutral-300 mb-6">
              We've sent a password reset link to <strong>{email}</strong>.
              Please check your email and follow the instructions to reset your password.
            </p>

            <div className="space-y-3">
              <p className="text-sm text-neutral-400">
                Didn't receive the email? Check your spam folder or try again.
              </p>

              <Link
                to="/login"
                className="btn-primary w-full py-3 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-surface-300/30">
              <p className="text-center text-xs text-white">
                © 2025 CATHS Asset Management System
                <br />
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat animate-fade-in"
      style={{
        backgroundImage: `url('/cathsbgfinal.png')`,
      }}
    >
      {/* Enhanced gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/60 via-primary-900/40 to-neutral-900/60"></div>

      {/* Glass morphism forgot password form */}
      <div className="relative z-10 w-full max-w-sm glass-card p-6 mx-4 animate-scale-in">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-primary-300 hover:text-primary-200 transition-colors duration-200 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Logo/Brand Section */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-primary-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">
            Forgot Password
          </h1>
          <p className="text-neutral-300 text-xs">
            Enter your email to reset your password
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label text-white">Email Address</label>
            <input
              type="email"
              placeholder="Enter your email address"
              className="form-input bg-surface-200/50 border-surface-300/50 text-white placeholder-neutral-400 focus:border-primary-400 focus:ring-primary-400"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="bg-error-500/20 border border-error-500/30 rounded-lg p-3">
              <p className="text-error-300 text-sm text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                <span>Send Reset Link</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-surface-300/30">
          <p className="text-center text-xs text-white">
            © 2025 CATHS Asset Management System
            <br />
            All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
